import { isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { ScheduledAutomationField } from '@shared/data/entity/fields/ScheduledAutomationField';
import { ScheduledAutomationEditField } from '@shared/scenes/entity/components/fields/ScheduledAutomationEditField';
import { ScheduledAutomationComponent } from '@shared/scenes/workflows/config';
import { isScheduledAutomationComponent } from '@shared/scenes/workflows/utils';
import { WorkflowComponentProps } from './WorkflowComponent';

export function WorkflowScheduledAutomationComponentLowCode(props: WorkflowComponentProps) {
  const wasDefaultUsed = useRef(false);
  const { setFieldError, errors, mode } = useCardFormContext();
  const { component } = props;

  useEffect(() => {
    if (
      mode === 'CREATE' &&
      isScheduledAutomationComponent(component) &&
      isNil(props.initialData) &&
      !wasDefaultUsed.current
    ) {
      wasDefaultUsed.current = true;
      props.onDataChanged?.(component.id, component.default);
    }
  }, [props, component, mode]);

  return (
    <WorkflowScheduledAutomationComponent
      {...props}
      setFieldError={setFieldError}
      errors={errors}
    />
  );
}

export function WorkflowScheduledAutomationComponent(
  props: WorkflowComponentProps & {
    setFieldError: (id: string, error: string) => void;
    errors: { [key: string]: string };
  },
) {
  const updateField = useCallback(
    (componentId: string, newField: ScheduledAutomationField | null) => {
      if (isNil(newField)) {
        props.onDataChanged?.(componentId, null);
      } else {
        props.onDataChanged?.(componentId, {
          automationTriggerDurations: newField.durations,
        } as any);
      }
    },
    [props],
  );

  const field = useMemo(() => {
    return ScheduledAutomationField.fromFieldJson(props.initialData);
  }, [props.initialData]);

  return (
    <ScheduledAutomationEditField
      field={field}
      {...props}
      component={props.component as ScheduledAutomationComponent}
      updateField={updateField}
      error={props.errors[props.component.id]}
      includeWrapperPadding={false}
    />
  );
}
